import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper,
  Select, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import TemplatePicker from '../../components/report/TemplatePicker';
import FieldPicker from '../../components/report/FieldPicker';
import FilterBuilder from '../../components/report/FilterBuilder';
import ResultView from '../../components/report/ResultView';
import {
  getTemplates, getFields, runAdhoc, getSaved, createSaved, updateSaved,
} from '../../lib/api/report';
import { buildSelection, parseSelection, friendlyErrorMessage, VIEW_TYPES, SHARE_TYPES } from '../../utils/reportSelection';

const SHARE_LABEL = { PRIVATE: '나만', COMPANY: '회사 전체' };
const VIEW_LABEL = { TABLE: '표', BAR: '막대', LINE: '선' };

/**
 * 보고서 작성 화면(한 화면, 계획 §6-2) - 템플릿 고르기 -> 필드/조건/그룹·집계 -> 미리보기 -> 저장.
 * 자동 실행하지 않는다 - "미리보기" 버튼을 눌러야 실행한다(계획 §6-2).
 */
const ReportComposeContainer = ({ rptUuid }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [templates, setTemplates] = useState([]);
  const [tmplCd, setTmplCd] = useState('');
  const [fields, setFields] = useState([]);

  const [mode, setMode] = useState('detail');
  const [columns, setColumns] = useState([]);
  const [groupBy, setGroupBy] = useState([]);
  const [aggregations, setAggregations] = useState([]);
  const [filters, setFilters] = useState([]);

  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  const [saveOpen, setSaveOpen] = useState(false);
  const [rptNm, setRptNm] = useState('');
  const [rptDesc, setRptDesc] = useState('');
  const [shareTyp, setShareTyp] = useState('PRIVATE');
  const [viewTyp, setViewTyp] = useState('TABLE');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getTemplates().then((res) => setTemplates(res.data || []));
  }, []);

  useEffect(() => {
    if (!rptUuid) return;
    getSaved(rptUuid).then((res) => {
      const saved = res.data;
      setTmplCd(saved.tmplCd);
      const sel = parseSelection(saved.selJson);
      setMode(sel.mode);
      setColumns(sel.columns);
      setGroupBy(sel.groupBy);
      setAggregations(sel.aggregations);
      setFilters(sel.filters);
      setRptNm(saved.rptNm);
      setRptDesc(saved.rptDesc || '');
      setShareTyp(saved.shareTyp);
      setViewTyp(saved.viewTyp);
    });
  }, [rptUuid]);

  useEffect(() => {
    if (!tmplCd) return;
    getFields(tmplCd).then((res) => setFields(res.data || []));
  }, [tmplCd]);

  const canChart = useMemo(
    () => mode === 'summary' && groupBy.length >= 1 && groupBy.length <= 2 && aggregations.filter((a) => a.field !== '*').length >= 1,
    [mode, groupBy, aggregations],
  );

  const selection = useMemo(
    () => buildSelection({ mode, columns, groupBy, aggregations, filters, limit: 100 }),
    [mode, columns, groupBy, aggregations, filters],
  );

  const handlePreview = () => {
    setRunning(true);
    runAdhoc(tmplCd, selection)
      .then((res) => setResult(res.data))
      .catch((err) => enqueueSnackbar(friendlyErrorMessage(err, '실행하지 못했습니다.'), { variant: 'error' }))
      .finally(() => setRunning(false));
  };

  const openSaveDialog = () => {
    if (!result) {
      enqueueSnackbar('먼저 미리보기를 실행해주세요.', { variant: 'warning' });
      return;
    }
    setSaveOpen(true);
  };

  const handleSave = () => {
    if (!rptNm.trim()) {
      enqueueSnackbar('보고서 이름을 입력해주세요.', { variant: 'warning' });
      return;
    }
    setSaving(true);
    const body = { rptNm: rptNm.trim(), rptDesc, tmplCd, selection, viewTyp, shareTyp };
    const req = rptUuid ? updateSaved(rptUuid, body) : createSaved(body);
    req
      .then((res) => {
        enqueueSnackbar('저장했습니다.', { variant: 'success' });
        navigate(`/reports/${res.data.rptUuid}`);
      })
      .catch((err) => enqueueSnackbar(friendlyErrorMessage(err, '저장하지 못했습니다.'), { variant: 'error' }))
      .finally(() => setSaving(false));
  };

  // 작성 화면은 데스크톱(md 이상) 전용 - 휴대폰은 보기만(계획 §6-2). 모바일 안내는 CSS로
  // 숨기지 않고 별도 문구로 보여준다(완전히 숨기면 "화면이 비었다"로 보인다).
  const mobileNotice = (
    <Box sx={{ display: { xs: 'block', md: 'none' }, p: 2 }}>
      <BreadcrumbsContainer title="분석 보고서" icon="chart" />
      <Typography color="text.secondary">보고서 작성은 데스크톱 화면에서 해주세요. 저장된 보고서는 휴대폰에서도 볼 수 있습니다.</Typography>
    </Box>
  );

  if (!tmplCd) {
    return (
      <>
        {mobileNotice}
        <Box sx={{ p: { xs: 1.5, sm: 2 }, display: { xs: 'none', md: 'block' } }}>
          <BreadcrumbsContainer title="분석 보고서/새 보고서" icon="chart" />
          <Typography variant="h6" sx={{ mb: 2 }}>템플릿 고르기</Typography>
          <TemplatePicker templates={templates} onPick={setTmplCd} />
        </Box>
      </>
    );
  }

  const tmpl = templates.find((t) => t.tmplCd === tmplCd);

  return (
    <>
      {mobileNotice}
      <Box sx={{ p: { xs: 1.5, sm: 2 }, display: { xs: 'none', md: 'block' } }}>
      <BreadcrumbsContainer title={`분석 보고서/${rptUuid ? '편집' : '새 보고서'}`} icon="chart" />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{tmpl?.tmplNm || tmplCd}</Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_e, v) => v && setMode(v)}
        >
          <ToggleButton value="detail">목록형</ToggleButton>
          <ToggleButton value="summary">요약형</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { md: '320px 1fr' } }}>
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>필드</Typography>
          <FieldPicker
            fields={fields}
            mode={mode}
            columns={columns}
            onColumnsChange={setColumns}
            groupBy={groupBy}
            onGroupByChange={setGroupBy}
            aggregations={aggregations}
            onAggregationsChange={setAggregations}
          />
        </Paper>

        <Box>
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>조건</Typography>
            <FilterBuilder fields={fields} filters={filters} onFiltersChange={setFilters} />
          </Paper>

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button variant="outlined" onClick={handlePreview} disabled={running}>
              미리보기
            </Button>
            <Button variant="contained" onClick={openSaveDialog}>저장</Button>
          </Box>

          {result && <ResultView result={result} viewTyp={result && canChart ? viewTyp : 'TABLE'} />}
        </Box>
      </Box>

      <Dialog open={saveOpen} onClose={() => setSaveOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>보고서 저장</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label="이름" value={rptNm} onChange={(e) => setRptNm(e.target.value)} fullWidth required />
          <TextField label="설명" value={rptDesc} onChange={(e) => setRptDesc(e.target.value)} fullWidth multiline minRows={2} />
          <Select value={shareTyp} onChange={(e) => setShareTyp(e.target.value)}>
            {SHARE_TYPES.map((s) => (
              <MenuItem key={s} value={s}>{SHARE_LABEL[s]}</MenuItem>
            ))}
          </Select>
          {canChart && (
            <Select value={viewTyp} onChange={(e) => setViewTyp(e.target.value)}>
              {VIEW_TYPES.map((v) => (
                <MenuItem key={v} value={v}>{VIEW_LABEL[v]}</MenuItem>
              ))}
            </Select>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveOpen(false)}>취소</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>저장</Button>
        </DialogActions>
      </Dialog>
      </Box>
    </>
  );
};

export default ReportComposeContainer;
